import type { Graph } from '@/schema/estate';

export async function ensureTag(
  g: Graph,
  opts: { estateId: string; groupId: string; name: string },
): Promise<string> {
  const existing = g.tag
    .all()
    .find((t) => t.estate_id === opts.estateId && t.name === opts.name && !t.archived);
  if (existing) return existing.id;
  const id = crypto.randomUUID();
  await g.tag.create({
    id,
    owner_group_id: opts.groupId,
    estate_id: opts.estateId,
    name: opts.name,
    archived: false,
  });
  return id;
}

/** The item's live links to any live tag row of this name. */
export function itemTagRowsNamed(
  g: Graph,
  opts: { itemId: string; estateId: string; name: string },
) {
  // Two devices tagging offline can each create a tag row of this name.
  const tagIds = new Set(
    g.tag
      .all()
      .filter((t) => t.estate_id === opts.estateId && t.name === opts.name && !t.archived)
      .map((t) => t.id),
  );
  return g.item_tag
    .all()
    .filter((r) => r.item_id === opts.itemId && tagIds.has(r.tag_id ?? '') && !r.archived);
}

export function itemTagNames(g: Graph, itemId: string): string[] {
  const tagIds = new Set(
    g.item_tag
      .all()
      .filter((r) => r.item_id === itemId && !r.archived)
      .map((r) => r.tag_id),
  );
  return g.tag
    .all()
    .filter((t) => tagIds.has(t.id) && !t.archived)
    .map((t) => t.name);
}

export async function addItemTag(
  g: Graph,
  opts: { itemId: string; estateId: string; groupId: string; name: string },
): Promise<void> {
  if (itemTagRowsNamed(g, opts).length > 0) return;
  const tagId = await ensureTag(g, opts);
  await g.item_tag.create({
    id: crypto.randomUUID(),
    owner_group_id: opts.groupId,
    item_id: opts.itemId,
    tag_id: tagId,
    created_at: Date.now(),
    archived: false,
  });
}

export async function removeItemTag(
  g: Graph,
  opts: { itemId: string; estateId: string; name: string },
): Promise<void> {
  for (const row of itemTagRowsNamed(g, opts)) {
    await g.item_tag.update(row.id, { archived: true });
  }
}
